import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import { TabularIcon, ImagingClassificationIcon, ImagingSegmentationIcon } from "../../../components/TaskTypeIcons";
import { CANADA_PROVINCES, VIEWBOX } from "../../../data/canadaProvinces";
import { resolveSiteGeo } from "../../../data/siteGeo";

// ── Theme constants (mirror the DashboardBanner navy/cyan vocabulary) ──
const ACCENT = "#7dd3fc";
const MAP_BG_TOP = "#13243f";
const MAP_BG_BOTTOM = "#0a1322";

const STATUS_META = {
  online: { label: "Online", color: "#34d399" },
  maintenance: { label: "Maintenance", color: "#fbbf24" },
  unknown: { label: "Unknown", color: "#94a3b8" },
  offline: { label: "Offline", color: "#64748b" },
};

const READINESS_COLOR = {
  ready: "#34d399",
  warming_up: "#fbbf24",
  needs_attention: "#f87171",
  unsupported: "#64748b",
};

const READINESS_LABEL = {
  ready: "Ready",
  warming_up: "Warming up",
  needs_attention: "Needs attention",
  unsupported: "Unsupported",
};

// Province → marker anchor (area-weighted centroid of the province's main
// landmass, in VIEWBOX coordinates). Used to drop a single dot in the visual
// middle of each province rather than fanning markers out by city.
const PROVINCE_CENTERS = {
  ab: { x: 237.3, y: 842.93 },
  bc: { x: 145.23, y: 844.95 },
  mb: { x: 390.99, y: 846.81 },
  nb: { x: 669.08, y: 969.23 },
  nl: { x: 708.56, y: 859.92 },
  nt: { x: 187.17, y: 674.7 },
  ns: { x: 691.14, y: 991.4 },
  nu: { x: 569.68, y: 151.06 },
  on: { x: 490.93, y: 913.45 },
  pe: { x: 697.34, y: 972.45 },
  qc: { x: 619.43, y: 860.07 },
  sk: { x: 314.86, y: 853.77 },
  yt: { x: 47.91, y: 690.93 },
};

const PROV_BY_ID = CANADA_PROVINCES.reduce((acc, p) => {
  acc[p.id] = p;
  return acc;
}, {});

const provinceCenter = (provId) => {
  if (PROVINCE_CENTERS[provId]) return PROVINCE_CENTERS[provId];
  const prov = PROV_BY_ID[provId];
  return prov ? { x: prov.bbox.x + prov.bbox.w / 2, y: prov.bbox.y + prov.bbox.h / 2 } : { x: 0, y: 0 };
};

// Status of a province cluster: worst-of its members (online unless something's off).
const clusterStatus = (members) => {
  if (members.some((m) => m.site.status === "offline")) return "offline";
  if (members.some((m) => m.site.status === "maintenance")) return "maintenance";
  if (members.every((m) => m.site.status === "online")) return "online";
  return "unknown";
};

const TaskTypeIcon = ({ type }) => {
  if (type === "imaging" || type === "imaging_classification" || type === "classification")
    return <ImagingClassificationIcon size={13} />;
  if (type === "segmentation" || type === "imaging_segmentation")
    return <ImagingSegmentationIcon size={13} />;
  if (type === "tabular") return <TabularIcon size={13} />;
  return <DatasetOutlinedIcon sx={{ fontSize: 13 }} />;
};

/**
 * Federation map — opens from the Dashboard banner's "X of Y sites online"
 * pill. Renders a projected map of Canada with interactive provinces; each
 * city shows a single marker (with a count when several sites share it) placed
 * on the city itself, and a synced sidebar lists every site with its datasets.
 * Hovering a province, marker, or sidebar card highlights the others.
 */
const SitesMapModal = ({ open, onClose, sites = [], capabilitiesBySite = {} }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  // active = { provId, siteIds: string[], scroll?: boolean }
  const [active, setActive] = useState(null);
  // popup = { provId, left, top } in map-container pixels (null = hidden)
  const [popup, setPopup] = useState(null);
  const cardRefs = useRef({});
  const mapRef = useRef(null);
  const svgRef = useRef(null);

  // Resolve each site → province; split out the unlocatable.
  const { placed, unlocated } = useMemo(() => {
    const placedArr = [];
    const unlocatedArr = [];
    (sites || []).forEach((site) => {
      const geo = resolveSiteGeo(site);
      const provId = geo ? (geo.province || "").toLowerCase() : null;
      const prov = provId ? PROV_BY_ID[provId] : null;
      if (!geo || !prov) {
        unlocatedArr.push(site);
        return;
      }
      placedArr.push({ site, geo, provId });
    });
    return { placed: placedArr, unlocated: unlocatedArr };
  }, [sites]);

  const sitesByProv = useMemo(() => {
    const map = {};
    placed.forEach((p) => (map[p.provId] = map[p.provId] || []).push(p));
    return map;
  }, [placed]);

  // One marker per province, dropped at the province's visual centre. The
  // count badge surfaces how many hospitals share that province.
  const provinceMarkers = useMemo(
    () =>
      Object.keys(sitesByProv).map((provId) => {
        const members = sitesByProv[provId];
        const center = provinceCenter(provId);
        return {
          provId,
          x: center.x,
          y: center.y,
          members,
          ids: members.map((m) => m.site.site_id),
          status: clusterStatus(members),
        };
      }),
    [sitesByProv]
  );

  // All datasets in a province, deduped by name across its sites, with the
  // number of hospitals offering each. Powers the on-hover popup.
  const datasetsByProv = useMemo(() => {
    const out = {};
    Object.keys(sitesByProv).forEach((provId) => {
      const byName = new Map();
      sitesByProv[provId].forEach((p) => {
        (capabilitiesBySite[p.site.site_id] || []).forEach((cap) => {
          const key = cap.display_name || cap.capability_id;
          const existing = byName.get(key);
          if (existing) {
            existing.siteCount += 1;
            if (cap.sample_count_estimate != null) existing.total += cap.sample_count_estimate;
          } else {
            byName.set(key, {
              key,
              display_name: cap.display_name,
              dataset_type: cap.dataset_type,
              readiness_status: cap.readiness_status,
              siteCount: 1,
              total: cap.sample_count_estimate != null ? cap.sample_count_estimate : null,
            });
          }
        });
      });
      out[provId] = Array.from(byName.values());
    });
    return out;
  }, [sitesByProv, capabilitiesBySite]);

  const hostProvinces = useMemo(() => new Set(placed.map((p) => p.provId)), [placed]);

  const onlineCount = (sites || []).filter((s) => s?.status === "online").length;
  const totalCount = (sites || []).length;

  // Sidebar grouping: province → sites.
  const grouped = useMemo(
    () =>
      Object.keys(sitesByProv)
        .map((id) => ({ prov: PROV_BY_ID[id], sites: sitesByProv[id] }))
        .sort((a, b) => a.prov.name.localeCompare(b.prov.name)),
    [sitesByProv]
  );

  const activeSiteIds = active?.siteIds || [];
  const isCardActive = (id) => activeSiteIds.includes(id);

  // Hover handlers ------------------------------------------------------
  // Project a province's VIEWBOX centre to map-container pixels so the popup
  // can be anchored to the dot regardless of the SVG's letterboxing.
  const positionPopup = useCallback((provId) => {
    const svg = svgRef.current;
    const container = mapRef.current;
    if (!svg || !container || typeof svg.createSVGPoint !== "function") {
      setPopup({ provId, left: null, top: null });
      return;
    }
    const c = provinceCenter(provId);
    const pt = svg.createSVGPoint();
    pt.x = c.x;
    pt.y = c.y;
    const ctm = svg.getScreenCTM();
    if (!ctm) {
      setPopup({ provId, left: null, top: null });
      return;
    }
    const sp = pt.matrixTransform(ctm);
    const rect = container.getBoundingClientRect();
    const left = Math.min(Math.max(sp.x - rect.left, 130), rect.width - 130);
    const top = sp.y - rect.top;
    setPopup({ provId, left, top });
  }, []);

  const enterProvince = useCallback(
    (provId, scroll = false) => {
      const list = sitesByProv[provId];
      if (!list || !list.length) return;
      setActive({ provId, siteIds: list.map((p) => p.site.site_id), scroll });
      positionPopup(provId);
    },
    [sitesByProv, positionPopup]
  );
  const enterCard = useCallback((p) => setActive({ provId: p.provId, siteIds: [p.site.site_id], scroll: false }), []);
  const leave = useCallback(() => {
    setActive(null);
    setPopup(null);
  }, []);

  // Scroll the relevant sidebar card into view when hovering the map.
  useEffect(() => {
    if (!active?.scroll || !active.siteIds.length) return;
    const el = cardRefs.current[active.siteIds[0]];
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [active]);

  const renderDatasetRow = (cap) => (
    <Box key={cap.capability_id} sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.4 }}>
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          bgcolor: "rgba(0,135,180,0.12)",
          color: "#0087B4",
        }}
      >
        <TaskTypeIcon type={cap.dataset_type} />
      </Box>
      <Typography sx={{ fontSize: "0.75rem", color: "var(--fx-text-primary)", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {cap.display_name}
      </Typography>
      {cap.sample_count_estimate != null && (
        <Typography sx={{ fontSize: "0.6875rem", color: "var(--fx-text-secondary)", flexShrink: 0 }}>
          ~{cap.sample_count_estimate}
        </Typography>
      )}
      <Box
        title={READINESS_LABEL[cap.readiness_status] || cap.readiness_status}
        sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: READINESS_COLOR[cap.readiness_status] || READINESS_COLOR.unsupported }}
      />
    </Box>
  );

  const renderSiteCard = (p) => {
    const caps = capabilitiesBySite[p.site.site_id] || [];
    const status = STATUS_META[p.site.status] || STATUS_META.unknown;
    const activeCard = isCardActive(p.site.site_id);
    return (
      <Box
        key={p.site.site_id}
        ref={(el) => (cardRefs.current[p.site.site_id] = el)}
        onMouseEnter={() => enterCard(p)}
        onMouseLeave={leave}
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: activeCard ? "var(--fx-border-strong)" : "var(--fx-border)",
          bgcolor: activeCard ? "var(--fx-inset)" : "var(--fx-paper)",
          boxShadow: activeCard ? "var(--fx-shadow-md)" : "none",
          cursor: "default",
          transition: "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: status.color, boxShadow: `0 0 6px ${status.color}`, flexShrink: 0 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "0.8125rem", color: "var(--fx-text-primary)", lineHeight: 1.2, flex: 1, minWidth: 0 }}>
            {p.site.display_name}
          </Typography>
        </Stack>
        {p.site.organization_display_name && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
            <BusinessIcon sx={{ fontSize: 12, color: "var(--fx-text-secondary)" }} />
            <Typography sx={{ fontSize: "0.7rem", color: "var(--fx-text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {p.site.organization_display_name} · {p.geo.city}
            </Typography>
          </Stack>
        )}
        <Box sx={{ mt: 0.75, pt: 0.5, borderTop: "1px dashed var(--fx-border)" }}>
          <Typography sx={{ fontSize: "0.5625rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--fx-text-secondary)", mb: 0.25 }}>
            {caps.length} dataset{caps.length === 1 ? "" : "s"}
          </Typography>
          {caps.length === 0 ? (
            <Typography sx={{ fontSize: "0.7rem", color: "var(--fx-text-secondary)", fontStyle: "italic" }}>None registered</Typography>
          ) : (
            caps.map(renderDatasetRow)
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          height: fullScreen ? "100%" : "86vh",
          bgcolor: "var(--fx-paper)",
          border: "1px solid var(--fx-border)",
        },
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          background: "linear-gradient(135deg, #1B365D 0%, #132643 60%, #0b1629 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: ACCENT,
            flexShrink: 0,
          }}
        >
          <PublicOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: ACCENT, fontSize: "0.625rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Federation Map
          </Typography>
          <Typography sx={{ color: "#fff", fontSize: "1.0625rem", fontWeight: 700, lineHeight: 1.2 }}>
            {totalCount} site{totalCount === 1 ? "" : "s"} across {hostProvinces.size} province{hostProvinces.size === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={`${onlineCount} of ${totalCount} online`}
          sx={{
            bgcolor: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "#86efac",
            fontWeight: 600,
            fontFamily: "var(--fx-font-mono)",
            fontVariantNumeric: "tabular-nums",
            mr: 0.5,
          }}
        />
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body: map + sidebar */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: { xs: "column", md: "row" } }}>
        {/* Map */}
        <Box
          ref={mapRef}
          sx={{
            position: "relative",
            flex: 1,
            minHeight: { xs: 320, md: 0 },
            background: `radial-gradient(120% 90% at 50% 0%, ${MAP_BG_TOP} 0%, ${MAP_BG_BOTTOM} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="svg"
            ref={svgRef}
            viewBox={`0 0 ${VIEWBOX.w} ${VIEWBOX.h}`}
            preserveAspectRatio="xMidYMid meet"
            sx={{ width: "100%", height: "100%", display: "block", p: 1.5 }}
            onMouseLeave={leave}
          >
            <defs>
              <radialGradient id="fx-marker-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={ACCENT} stopOpacity="0.55" />
                <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* Provinces (interactive) */}
            {CANADA_PROVINCES.map((p) => {
              const isHost = hostProvinces.has(p.id);
              const isActive = active?.provId === p.id;
              const fill = isActive
                ? "rgba(0,135,180,0.45)"
                : isHost
                ? "rgba(0,135,180,0.22)"
                : "rgba(125,211,252,0.05)";
              const stroke = isActive
                ? ACCENT
                : isHost
                ? "rgba(125,211,252,0.6)"
                : "rgba(125,211,252,0.22)";
              return (
                <path
                  key={p.id}
                  d={p.path}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={isActive ? 1.8 : isHost ? 1.1 : 0.7}
                  strokeLinejoin="round"
                  onMouseEnter={() => enterProvince(p.id)}
                  style={{ cursor: isHost ? "pointer" : "default", transition: "fill 0.18s ease, stroke 0.18s ease" }}
                />
              );
            })}

            {/* Province markers (one centred dot per province, with count) */}
            {provinceMarkers.map((c) => {
              const status = STATUS_META[c.status] || STATUS_META.unknown;
              const isActive = active?.provId === c.provId;
              const online = c.status === "online";
              const count = c.members.length;
              const baseR = count > 1 ? 11 : 7;
              const r = isActive ? baseR + 2.5 : baseR;
              return (
                <g
                  key={c.provId}
                  transform={`translate(${c.x}, ${c.y})`}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => enterProvince(c.provId, true)}
                  onMouseLeave={leave}
                >
                  <circle r={r + 8} fill="transparent" />
                  {online && (
                    <circle r={isActive ? r + 9 : r + 5} fill="url(#fx-marker-glow)">
                      <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2.4s" repeatCount="indefinite" />
                    </circle>
                  )}
                  <circle r={r} fill={status.color} stroke="#0a1322" strokeWidth="1.8" />
                  {count > 1 ? (
                    <text textAnchor="middle" dominantBaseline="central" fontSize={isActive ? 12 : 11} fontWeight="700" fill="#0a1322">
                      {count}
                    </text>
                  ) : (
                    <circle r={isActive ? 2.6 : 2} fill="#fff" opacity="0.92" />
                  )}
                </g>
              );
            })}
          </Box>

          {/* Province dataset popup (on hover) */}
          {popup && popup.left != null && (() => {
            const prov = PROV_BY_ID[popup.provId];
            const provSites = sitesByProv[popup.provId] || [];
            const datasets = datasetsByProv[popup.provId] || [];
            const shown = datasets.slice(0, 8);
            const extra = datasets.length - shown.length;
            return (
              <Box
                sx={{
                  position: "absolute",
                  left: popup.left,
                  top: popup.top,
                  transform: "translate(-50%, calc(-100% - 18px))",
                  width: 260,
                  maxWidth: "calc(100% - 24px)",
                  zIndex: 5,
                  pointerEvents: "none",
                  borderRadius: 1.5,
                  bgcolor: "rgba(10,19,34,0.94)",
                  border: "1px solid rgba(125,211,252,0.28)",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
                  backdropFilter: "blur(8px)",
                  overflow: "hidden",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "50%",
                    bottom: -7,
                    transform: "translateX(-50%) rotate(45deg)",
                    width: 12,
                    height: 12,
                    bgcolor: "rgba(10,19,34,0.94)",
                    borderRight: "1px solid rgba(125,211,252,0.28)",
                    borderBottom: "1px solid rgba(125,211,252,0.28)",
                  },
                }}
              >
                <Box sx={{ px: 1.25, py: 0.9, borderBottom: "1px solid rgba(125,211,252,0.16)", display: "flex", alignItems: "center", gap: 0.75 }}>
                  <PlaceOutlinedIcon sx={{ fontSize: 15, color: ACCENT }} />
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#fff", flex: 1, minWidth: 0 }}>
                    {prov?.name || popup.provId}
                  </Typography>
                  <Typography sx={{ fontSize: "0.6875rem", color: "rgba(255,255,255,0.65)", fontFamily: "var(--fx-font-mono)" }}>
                    {provSites.length} site{provSites.length === 1 ? "" : "s"} · {datasets.length} dataset{datasets.length === 1 ? "" : "s"}
                  </Typography>
                </Box>
                <Box sx={{ px: 1.25, py: 0.75 }}>
                  {datasets.length === 0 ? (
                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)", fontStyle: "italic" }}>
                      No datasets registered
                    </Typography>
                  ) : (
                    shown.map((d) => (
                      <Box key={d.key} sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.35 }}>
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: 0.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            bgcolor: "rgba(125,211,252,0.14)",
                            color: ACCENT,
                          }}
                        >
                          <TaskTypeIcon type={d.dataset_type} />
                        </Box>
                        <Typography sx={{ fontSize: "0.75rem", color: "#fff", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {d.display_name}
                        </Typography>
                        {d.siteCount > 1 && (
                          <Typography sx={{ fontSize: "0.625rem", color: "rgba(255,255,255,0.5)", flexShrink: 0 }}>
                            ×{d.siteCount}
                          </Typography>
                        )}
                        {d.total != null && (
                          <Typography sx={{ fontSize: "0.6875rem", color: "rgba(255,255,255,0.7)", flexShrink: 0, fontFamily: "var(--fx-font-mono)" }}>
                            ~{d.total}
                          </Typography>
                        )}
                        <Box
                          sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: READINESS_COLOR[d.readiness_status] || READINESS_COLOR.unsupported }}
                        />
                      </Box>
                    ))
                  )}
                  {extra > 0 && (
                    <Typography sx={{ fontSize: "0.6875rem", color: "rgba(255,255,255,0.6)", pt: 0.4 }}>
                      +{extra} more dataset{extra === 1 ? "" : "s"}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })()}

          {/* Legend */}
          <Box
            sx={{
              position: "absolute",
              left: 14,
              bottom: 12,
              display: "flex",
              gap: 1.25,
              px: 1.25,
              py: 0.75,
              borderRadius: 1.5,
              bgcolor: "rgba(10,19,34,0.6)",
              border: "1px solid rgba(125,211,252,0.18)",
              backdropFilter: "blur(6px)",
            }}
          >
            {[["online", "Online"], ["maintenance", "Maintenance"], ["offline", "Offline"]].map(([k, label]) => (
              <Stack key={k} direction="row" alignItems="center" spacing={0.6}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS_META[k].color }} />
                <Typography sx={{ fontSize: "0.6875rem", color: "rgba(255,255,255,0.8)" }}>{label}</Typography>
              </Stack>
            ))}
          </Box>
        </Box>

        {/* Sidebar */}
        <Box
          sx={{
            width: { xs: "100%", md: 340 },
            flexShrink: 0,
            borderLeft: { md: "1px solid var(--fx-border)" },
            borderTop: { xs: "1px solid var(--fx-border)", md: "none" },
            bgcolor: "var(--fx-inset)",
            overflowY: "auto",
            p: 1.5,
          }}
        >
          {grouped.map(({ prov, sites: provSites }) => {
            const provActive = active?.provId === prov.id;
            return (
              <Box key={prov.id} sx={{ mb: 1.5 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  onMouseEnter={() => enterProvince(prov.id)}
                  onMouseLeave={leave}
                  sx={{
                    mb: 0.75,
                    px: 0.5,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: "default",
                    bgcolor: provActive ? "rgba(0,135,180,0.12)" : "transparent",
                    transition: "background-color 0.15s ease",
                  }}
                >
                  <PlaceOutlinedIcon sx={{ fontSize: 15, color: "#0087B4" }} />
                  <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--fx-text-primary)" }}>
                    {prov.name}
                  </Typography>
                  <Typography sx={{ fontSize: "0.6875rem", color: "var(--fx-text-secondary)" }}>
                    · {provSites.length} site{provSites.length === 1 ? "" : "s"}
                  </Typography>
                </Stack>
                <Stack spacing={1}>{provSites.map(renderSiteCard)}</Stack>
              </Box>
            );
          })}

          {unlocated.length > 0 && (
            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid var(--fx-border)" }}>
              <Typography sx={{ fontSize: "0.6875rem", color: "var(--fx-text-secondary)", mb: 0.5 }}>
                No location for:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {unlocated.map((s) => (
                  <Chip key={s.site_id} size="small" label={s.display_name} sx={{ bgcolor: "var(--fx-paper)", border: "1px solid var(--fx-border-strong)", color: "var(--fx-text-primary)", fontSize: "0.7rem" }} />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default SitesMapModal;
